import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { Position } from "./types";

const MONTHS_NO = [
  "jan",
  "feb",
  "mar",
  "apr",
  "mai",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "des",
];

const WIND_DIR_NO: Record<string, string> = {
  N: "nord", NNE: "nord-nordøst", NE: "nordøst", ENE: "øst-nordøst",
  E: "øst", ESE: "øst-sørøst", SE: "sørøst", SSE: "sør-sørøst",
  S: "sør", SSW: "sør-sørvest", SW: "sørvest", WSW: "vest-sørvest",
  W: "vest", WNW: "vest-nordvest", NW: "nordvest", NNW: "nord-nordvest",
};

const CARDINALS = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];

function windDirNO(direction: string | null): string | null {
  if (!direction) return null;
  if (WIND_DIR_NO[direction]) return WIND_DIR_NO[direction];
  const deg = parseFloat(direction);
  if (!isNaN(deg)) {
    const card = CARDINALS[Math.round(deg / 22.5) % 16]!;
    return WIND_DIR_NO[card] ?? card;
  }
  return direction;
}

function beaufortNO(ms: number): string {
  if (ms < 0.3) return "stille";
  if (ms < 1.6) return "flau vind";
  if (ms < 3.4) return "svak vind";
  if (ms < 5.5) return "lett bris";
  if (ms < 8.0) return "laber bris";
  if (ms < 10.8) return "frisk bris";
  if (ms < 13.9) return "liten kuling";
  if (ms < 17.2) return "stiv kuling";
  if (ms < 20.8) return "sterk kuling";
  if (ms < 24.5) return "liten storm";
  if (ms < 28.5) return "full storm";
  if (ms < 32.7) return "sterk storm";
  return "orkan";
}

function formatTime(ts: string) {
  const d = new Date(ts);
  return `${d.getDate()}. ${MONTHS_NO[d.getMonth()]} ${d.getFullYear()} kl. ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function popupHTML(p: Position) {
  const parts: string[] = [];
  if (p.placename) parts.push(`<strong>${p.placename}</strong>`);
  if (p.altitude != null) parts.push(`${Math.round(p.altitude)} moh.`);
  parts.push(`<span style="color:#666">${formatTime(p.timestamp)}</span>`);
  if (p.temperature != null && p.wind_speed != null) {
    const dir = windDirNO(p.wind_direction) ?? "";
    const beaufort = beaufortNO(p.wind_speed);
    const iconHTML = p.weather_symbol
      ? `<img src="https://cdn.jsdelivr.net/gh/metno/weathericons/weather/svg/${p.weather_symbol}.svg" width="36" height="36" style="float:left;margin:-2px 8px 4px 0;vertical-align:middle">`
      : "";
    parts.push(
      `${iconHTML}${p.temperature}°C · ${beaufort}<br style="clear:none">${p.wind_speed} m/s fra ${dir}`,
    );
  }
  return `<div style="font:13px system-ui,sans-serif;line-height:1.7">${parts.map((s) => `<div>${s}</div>`).join("")}</div>`;
}

function createPulsingMarker(): HTMLElement {
  const el = document.createElement("div");
  el.style.cssText = "position:relative;width:12px;height:12px";

  const dot = document.createElement("div");
  dot.style.cssText = `
    width:12px;height:12px;border-radius:50%;
    background:orange;border:2px solid #333;
    position:absolute;top:0;left:0;z-index:1;
  `;

  const ring = document.createElement("div");
  ring.style.cssText = `
    position:absolute;
    width:12px;height:12px;
    border-radius:50%;
    border:3px solid orange;
    animation:pulse 2s ease-out infinite;
    top:0;left:0;
  `;

  const style = document.createElement("style");
  style.textContent = `
    @keyframes pulse {
      from { transform:scale(1); opacity:1; }
      to   { transform:scale(4); opacity:0; }
    }
  `;
  document.head.appendChild(style);

  el.appendChild(ring);
  el.appendChild(dot);
  return el;
}

interface Props {
  positions: Position[];
  hoveredIndex: number | null;
  onHover: (index: number | null) => void;
  status: string;
}

export default function TrackMap({ positions, hoveredIndex, onHover, status }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const liveMarkerRef = useRef<maplibregl.Marker | null>(null);
  const initDoneRef = useRef(false);
  const positionsRef = useRef(positions);
  const onHoverRef = useRef(onHover);
  positionsRef.current = positions;
  onHoverRef.current = onHover;

  // Initialise map once
  useEffect(() => {
    const map = new maplibregl.Map({
      container: containerRef.current!,
      style: {
        version: 8,
        sources: {
          kartverket: {
            type: "raster",
            tiles: [
              "https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png",
            ],
            tileSize: 256,
            attribution: "© <a href='https://kartverket.no/'>Kartverket</a>",
          },
        },
        layers: [{ id: "kartverket", type: "raster", source: "kartverket" }],
      },
      center: [10, 62],
      zoom: 5,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-left");
    mapRef.current = map;
    return () => map.remove();
  }, []);

  // Draw track when positions load
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !positions.length || initDoneRef.current) return;

    function draw() {
      if (!map) return;
      initDoneRef.current = true;

      const coords = positions.map(
        (p) => [p.longitude, p.latitude] as [number, number],
      );

      map.addSource("track", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: { type: "LineString", coordinates: coords },
          properties: {},
        },
      });
      map.addLayer({
        id: "track-line",
        type: "line",
        source: "track",
        paint: {
          "line-color": "#333",
          "line-width": 2,
          "line-opacity": 0.8,
          "line-dasharray": [4, 4],
        },
      });

      map.addSource("positions", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: positions.map((p) => ({
            type: "Feature" as const,
            geometry: {
              type: "Point" as const,
              coordinates: [p.longitude, p.latitude],
            },
            properties: { ...p },
          })),
        },
      });
      map.addLayer({
        id: "positions-circle",
        type: "circle",
        source: "positions",
        minzoom: 10,
        paint: {
          "circle-radius": 5,
          "circle-color": "#333",
          "circle-stroke-width": 1.5,
          "circle-stroke-color": "#fff",
        },
      });

      // Fit bounds
      const bounds = coords.reduce(
        (b, c) => b.extend(c),
        new maplibregl.LngLatBounds(coords[0], coords[0]),
      );
      map.fitBounds(bounds, { padding: 60, maxZoom: 14 });

      // Popup on click
      const popup = new maplibregl.Popup({
        closeButton: false,
        maxWidth: "280px",
      });
      map.on("click", "positions-circle", (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        popup
          .setLngLat(
            (feat.geometry as GeoJSON.Point).coordinates as [number, number],
          )
          .setHTML(popupHTML(feat.properties as Position))
          .addTo(map);
      });
      map.on("mouseenter", "positions-circle", (e) => {
        map.getCanvas().style.cursor = "pointer";
        const feat = e.features?.[0];
        if (!feat) return;
        const idx = positionsRef.current.findIndex((p) => p.id === feat.properties?.id);
        if (idx >= 0) onHoverRef.current(idx);
      });
      map.on("mouseleave", "positions-circle", () => {
        map.getCanvas().style.cursor = "";
        onHoverRef.current(null);
      });

      // Hover position source + layer (updated via setData when hoveredIndex changes)
      map.addSource("hover-position", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "hover-circle",
        type: "circle",
        source: "hover-position",
        paint: {
          "circle-radius": 7,
          "circle-color": "#fff",
          "circle-stroke-width": 2.5,
          "circle-stroke-color": "#333",
        },
      });

      // Pulsing live marker for the latest position
      const last = positions[positions.length - 1];
      if (last) {
        liveMarkerRef.current = new maplibregl.Marker({
          element: createPulsingMarker(),
        })
          .setLngLat([last.longitude, last.latitude])
          .addTo(map);
      }
    }

    if (map.isStyleLoaded()) {
      draw();
    } else {
      map.once("load", draw);
    }
  }, [positions]);

  // Update hover GeoJSON source when hoveredIndex changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const source = map.getSource("hover-position") as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    const p = hoveredIndex != null ? positions[hoveredIndex] : null;
    source.setData({
      type: "FeatureCollection",
      features: p
        ? [{ type: "Feature", geometry: { type: "Point", coordinates: [p.longitude, p.latitude] }, properties: {} }]
        : [],
    });
  }, [hoveredIndex, positions]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "rgba(255,255,255,0.93)",
          padding: "8px 12px",
          borderRadius: 5,
          boxShadow: "0 1px 6px rgba(0,0,0,0.2)",
          fontSize: 12,
          maxWidth: 320,
          zIndex: 10,
        }}
      >
        <strong style={{ display: "block", marginBottom: 2 }}>MyTrack</strong>
        {status}
      </div>
    </div>
  );
}
